import { Plus, X } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  ASSISTANT_MAX_EXAMPLES,
  type AssistantExample,
} from '@sdr-agend/admin-schemas'

interface AssistantExamplesStepProps {
  examples: AssistantExample[]
  onChange: (next: AssistantExample[]) => void
  disabled?: boolean
}

/**
 * Story 3.18 §AC #7 — Step 3: Examples (optional).
 *
 * Up to 5 Q/A pairs to seed the meta-prompt with "what the conversation
 * should look like". Each row is removable; the list grows up to
 * ASSISTANT_MAX_EXAMPLES.
 */
export function AssistantExamplesStep({
  examples,
  onChange,
  disabled = false,
}: AssistantExamplesStepProps): React.ReactElement {
  const atCap = examples.length >= ASSISTANT_MAX_EXAMPLES

  function update(idx: number, patch: Partial<AssistantExample>): void {
    onChange(
      examples.map((ex, i) => (i === idx ? ({ ...ex, ...patch } as AssistantExample) : ex)),
    )
  }
  function add(): void {
    if (atCap) return
    onChange([...examples, { q: '', a: '' }])
  }
  function remove(idx: number): void {
    onChange(examples.filter((_, i) => i !== idx))
  }

  return (
    <div className="grid gap-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Adicione exemplos de pergunta-resposta esperados (opcional, até{' '}
          {ASSISTANT_MAX_EXAMPLES}).
        </p>
        <Button type="button" variant="outline" onClick={add} disabled={disabled || atCap}>
          <Plus className="h-4 w-4" aria-hidden="true" />
          Adicionar exemplo
        </Button>
      </div>

      {examples.length === 0 && (
        <p className="rounded-md border border-dashed p-3 text-xs text-muted-foreground">
          Nenhum exemplo adicionado. Exemplos ajudam o LLM a calibrar o tom esperado.
        </p>
      )}

      <ul className="grid gap-3" aria-label="Exemplos de interação">
        {examples.map((ex, idx) => (
          <li key={idx} className="grid gap-2 rounded-md border bg-muted/20 p-3">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-muted-foreground">
                Exemplo {idx + 1}
              </span>
              <button
                type="button"
                aria-label={`Remover exemplo ${idx + 1}`}
                onClick={() => remove(idx)}
                disabled={disabled}
                className="rounded-full p-1 hover:bg-destructive/10 hover:text-destructive"
              >
                <X className="h-3 w-3" aria-hidden="true" />
              </button>
            </div>
            <div className="grid gap-2">
              <Label htmlFor={`assistant-example-q-${idx}`}>Pergunta do cliente</Label>
              <Input
                id={`assistant-example-q-${idx}`}
                value={ex.q}
                onChange={(e) => update(idx, { q: e.target.value })}
                disabled={disabled}
                maxLength={500}
                placeholder="Ex: Posso remarcar para amanhã?"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor={`assistant-example-a-${idx}`}>Resposta esperada</Label>
              <Input
                id={`assistant-example-a-${idx}`}
                value={ex.a}
                onChange={(e) => update(idx, { a: e.target.value })}
                disabled={disabled}
                maxLength={500}
                placeholder="Ex: Claro! Tenho horários X e Y disponíveis."
              />
            </div>
          </li>
        ))}
      </ul>
    </div>
  )
}
